import { FC, useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ClientFormSchema } from "../../AddClientSteps/lib";
import styles from "../AddClientDrawerSteps.module.scss";
import { CopySourceEntity } from "./lib";
import { SetupStepFooter } from "./SetupStepFooter";

const ENTITY_PLACEHOLDER = "Select entity";

interface Props {
    entities: CopySourceEntity[];
    onNext: () => void;
}

export const CoaEntitySourceSelect: FC<Props> = ({ entities, onNext }) => {
    const { setFieldValue, values } = useFormikContext<ClientFormSchema>();
    const selectedEntityId = values.coaSourceEntityId;
    const isSourceSelected = selectedEntityId !== null;

    const entityItems = useMemo(
        () =>
            entities.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "Untitled entity",
            })),
        [entities],
    );
    const selectedEntityName = entityItems.find(
        (item) => item.value === selectedEntityId,
    )?.label;

    const backToPicker = useCallback(() => {
        setFieldValue("coaSetupMethod", "kick");
        setFieldValue("coaSourceEntityId", null);
    }, [setFieldValue]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>Copy from entity</div>
            <div className={styles.subheader}>
                Select an entity to copy its Chart of Accounts.
            </div>

            <div className={styles.coaSourceField}>
                <div className={styles.migrationSectionLabel}>
                    Source entity
                </div>
                <Picker.Select
                    items={entityItems}
                    searchable
                    value={selectedEntityId}
                    onValueChange={(entityId) => {
                        if (entityId !== null) {
                            setFieldValue("coaSourceEntityId", entityId);
                        }
                    }}
                    placeholder={ENTITY_PLACEHOLDER}
                    renderValue={() => selectedEntityName ?? ENTITY_PLACEHOLDER}
                    trigger={
                        <Picker.Button variant="primary" size="md" block />
                    }
                />
            </div>

            <SetupStepFooter
                onBack={backToPicker}
                onNext={onNext}
                isNextEnabled={isSourceSelected}
            />
        </div>
    );
};
